/**
 * PipelineBar
 *
 * TWO-AXIS pipeline board widget, per docs/architecture/POST-STATUS-MODEL.md
 * "Pipeline board" section:
 *
 *   (A) Funnel bar — workflow + content axis ("how much work is moving
 *       toward being scheduled"): Drafts -> Awaiting approval -> Changes
 *       requested -> Approved -> Scheduled. When the team is Carte Blanche
 *       (`pipeline.carteBlanche`), the approval stages (Awaiting approval /
 *       Changes requested / Approved) DO NOT EXIST and are never rendered —
 *       a CB post goes straight from Draft to Scheduled.
 *
 *   (B) Outcome tiles — publishing axis, first-class (never folded into each
 *       other): Published / Partial success / Failed / Missed. Rendered as
 *       its OWN group beneath the funnel — the doc is explicit that the
 *       funnel and the outcomes must never be flattened into one bucket-per-
 *       post partition, so they never share a bar/total with (A).
 *
 * Both groups degrade gracefully: an all-zero funnel shows the existing
 * dashed empty-state bar; the outcomes group only renders at all when it has
 * something to show.
 */

import { useTranslation } from 'react-i18next';
import { GlossaryTooltip } from './GlossaryTooltip';
import type { PipelineBreakdown } from '@/data/portfolio.types';

interface PipelineBarProps {
  pipeline: PipelineBreakdown;
  /** When true renders a tiny colour legend above the bar (first row only) */
  showLegend?: boolean;
  /** When true the wrapper fills 100% width instead of the fixed 140px cell size */
  fullWidth?: boolean;
  /** On mobile, do not wrap in the tap-to-open glossary sheet (use inside tappable cards). */
  disableGlossaryOnMobile?: boolean;
  /** Show the meaning of each count on compact cards. */
  showCountLabels?: boolean;
}

type FunnelKey = 'unscheduled' | 'awaitingApproval' | 'changesRequested' | 'approved' | 'scheduled';
type OutcomeKey = 'publishedThisWeek' | 'partial' | 'failed' | 'missed';

interface SegmentConfig<K extends string> {
  key: K;
  /** i18n key for the label — resolved via t() at render time (these arrays
   *  are module-level consts so they can't call t() directly). Reuses the
   *  shared `status_*` keys (common.json, via i18n's fallbackNS) wherever the
   *  label IS a plain status; funnel-only concepts (Drafts / Awaiting
   *  approval / Changes) get their own `pipeline_*` keys. */
  labelKey: string;
  labelDefault: string;
  bg: string;
  dotBg: string;
  text: string;
}

// (A) Funnel — workflow + content axis, left-to-right along the production
// workflow. `approvalOnly` segments are hidden entirely for carte-blanche
// teams (POST-STATUS-MODEL.md: "the approval stages ... DO NOT EXIST").
const FUNNEL_SEGMENTS: Array<SegmentConfig<FunnelKey> & { approvalOnly?: boolean }> = [
  { key: 'unscheduled',      labelKey: 'pipeline_drafts',            labelDefault: 'Drafts',            bg: 'bg-gray-300',      dotBg: 'bg-gray-400',      text: 'text-gray-600' },
  { key: 'awaitingApproval', labelKey: 'pipeline_awaiting_approval', labelDefault: 'Awaiting release', bg: 'bg-amber-300',     dotBg: 'bg-amber-400',     text: 'text-amber-700',   approvalOnly: true },
  { key: 'changesRequested', labelKey: 'pipeline_changes_requested', labelDefault: 'Changes',           bg: 'bg-coral-300',     dotBg: 'bg-coral-400',     text: 'text-coral-800',   approvalOnly: true },
  { key: 'approved',         labelKey: 'status_approved',            labelDefault: 'Approved',          bg: 'bg-primary-400',   dotBg: 'bg-primary-500',   text: 'text-primary-900', approvalOnly: true },
  { key: 'scheduled',        labelKey: 'status_scheduled',           labelDefault: 'Scheduled',         bg: 'bg-secondary-300', dotBg: 'bg-secondary-400', text: 'text-gray-700' },
];

// (B) Outcome tiles — publishing axis, first-class (never folded).
const OUTCOME_SEGMENTS: Array<SegmentConfig<OutcomeKey>> = [
  { key: 'publishedThisWeek', labelKey: 'status_published', labelDefault: 'Published',       bg: 'bg-primary-500', dotBg: 'bg-primary-500', text: 'text-primary-700' },
  { key: 'partial',           labelKey: 'status_partial',   labelDefault: 'Partial success', bg: 'bg-amber-400',   dotBg: 'bg-amber-400',   text: 'text-amber-700' },
  { key: 'failed',            labelKey: 'status_failed',    labelDefault: 'Failed',          bg: 'bg-coral-500',   dotBg: 'bg-coral-500',   text: 'text-coral-700' },
  { key: 'missed',            labelKey: 'status_missed',    labelDefault: 'Missed',          bg: 'bg-gray-500',    dotBg: 'bg-gray-500',    text: 'text-gray-600' },
];

// Minimum visual width per non-zero segment, same rule for both bars.
const MIN_PCT = 8;

function segmentWidths(counts: number[], total: number): number[] {
  if (total === 0) return counts.map(() => 0);
  const nonZero = counts.filter((c) => c > 0).length;
  const reservedPct = nonZero * MIN_PCT;
  const remainingPct = Math.max(0, 100 - reservedPct);
  return counts.map((count) => (count === 0 ? 0 : MIN_PCT + (count / total) * remainingPct));
}

export const PipelineBar = ({ pipeline, showLegend = false, fullWidth = false, disableGlossaryOnMobile = false, showCountLabels = false }: PipelineBarProps) => {
  const { t } = useTranslation('agency');

  const funnelSegments = FUNNEL_SEGMENTS.filter((seg) => !seg.approvalOnly || !pipeline.carteBlanche).map(
    (seg) => ({ ...seg, label: t(seg.labelKey, { defaultValue: seg.labelDefault }) }),
  );
  const funnelCounts = funnelSegments.map((seg) => pipeline[seg.key] ?? 0);
  const funnelTotal = funnelCounts.reduce((sum, c) => sum + c, 0);
  const funnelWidths = segmentWidths(funnelCounts, funnelTotal);

  const outcomeSegments = OUTCOME_SEGMENTS.map((seg) => ({
    ...seg,
    label: t(seg.labelKey, { defaultValue: seg.labelDefault }),
  }));
  const outcomeCounts = outcomeSegments.map((seg) => pipeline[seg.key] ?? 0);
  const outcomeTotal = outcomeCounts.reduce((sum, c) => sum + c, 0);
  const outcomeWidths = segmentWidths(outcomeCounts, outcomeTotal);

  const wrapperStyle = fullWidth ? { width: '100%' } : { width: 140 };

  if (funnelTotal === 0 && outcomeTotal === 0) {
    const emptyContent = (
      <div className="space-y-1" style={wrapperStyle}>
        {/* Dashed empty bar */}
        <div
          className="h-2 rounded-full border border-dashed border-gray-300"
          role="img"
          aria-label={t('pipeline_bar_empty', { defaultValue: 'No content in pipeline' })}
        />
        <p className="text-[10px] text-gray-400 leading-none">
          {t('pipeline_bar_empty', { defaultValue: 'No content in pipeline' })}
        </p>
      </div>
    );

    return (
      <GlossaryTooltip termId="metric-pipeline" side="top" fullWidth={fullWidth} disableOnMobile={disableGlossaryOnMobile}>
        {emptyContent}
      </GlossaryTooltip>
    );
  }

  const barContent = (
    <div className="space-y-1" style={wrapperStyle}>
      {/* Optional legend - shown on first row */}
      {showLegend && (
        <div className="flex items-center gap-2 flex-wrap">
          {funnelSegments.map((seg) => (
            <span key={seg.key} className="inline-flex items-center gap-0.5">
              <span className={`w-1.5 h-1.5 rounded-full ${seg.dotBg} shrink-0`} aria-hidden="true" />
              <span className="text-[9px] text-gray-400 leading-none">{seg.label}</span>
            </span>
          ))}
        </div>
      )}

      {/* (A) Funnel bar — workflow + content axis */}
      {funnelTotal > 0 ? (
        <div
          className="flex h-2 rounded-full overflow-hidden cursor-default"
          role="img"
          aria-label={t('pipeline_bar_aria_label', {
            defaultValue: 'Funnel: {{counts}}',
            counts: funnelSegments
              .map((seg, idx) => `${funnelCounts[idx]} ${seg.label.toLowerCase()}`)
              .join(', '),
          })}
        >
          {funnelSegments.map((seg, idx) => {
            const count = funnelCounts[idx];
            if (count === 0) return null;
            return (
              <div
                key={seg.key}
                className={`${seg.bg} h-full shrink-0 transition-all`}
                style={{ width: `${funnelWidths[idx]}%`, marginLeft: idx > 0 ? '1px' : 0 }}
              />
            );
          })}
        </div>
      ) : (
        <div className="h-2 rounded-full border border-dashed border-gray-300" aria-hidden="true" />
      )}

      {/* Funnel compact count readout: e.g. ● 3  ● 1  ● 2  ● 4 */}
      {funnelTotal > 0 && (
        <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
          {funnelSegments.map((seg, idx) => {
            const count = funnelCounts[idx];
            if (count === 0) return null;
            return (
              <span key={seg.key} className="inline-flex items-center gap-1">
                <span className={`w-1.5 h-1.5 rounded-full ${seg.dotBg} shrink-0`} aria-hidden="true" />
                <span className={`text-[10px] font-semibold tabular-nums ${seg.text}`}>{count}</span>
                {showCountLabels && <span className="text-[10px] text-gray-600">{seg.label}</span>}
              </span>
            );
          })}
          <span className={`text-[10px] text-gray-400 ml-auto tabular-nums ${showCountLabels ? 'hidden lg:inline' : ''}`}>{funnelTotal}</span>
        </div>
      )}

      {/* (B) Outcome tiles — publishing axis, first-class, own group (never
          merged into the funnel bar/total above). Renders only when there is
          something to show. */}
      {outcomeTotal > 0 && (
        <>
          <div
            className="flex h-1.5 rounded-full overflow-hidden cursor-default mt-1.5"
            role="img"
            aria-label={t('pipeline_bar_outcomes_aria_label', {
              defaultValue: 'Outcomes: {{counts}}',
              counts: outcomeSegments
                .map((seg, idx) => `${outcomeCounts[idx]} ${seg.label.toLowerCase()}`)
                .join(', '),
            })}
          >
            {outcomeSegments.map((seg, idx) => {
              const count = outcomeCounts[idx];
              if (count === 0) return null;
              return (
                <div
                  key={seg.key}
                  className={`${seg.bg} h-full shrink-0 transition-all`}
                  style={{ width: `${outcomeWidths[idx]}%`, marginLeft: idx > 0 ? '1px' : 0 }}
                />
              );
            })}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {outcomeSegments.map((seg, idx) => {
              const count = outcomeCounts[idx];
              if (count === 0) return null;
              return (
                <span key={seg.key} className="inline-flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${seg.dotBg} shrink-0`} aria-hidden="true" />
                  <span className={`text-[10px] font-semibold tabular-nums ${seg.text}`}>{count}</span>
                  {showCountLabels && <span className="text-[10px] text-gray-600">{seg.label}</span>}
                </span>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  return (
    <GlossaryTooltip termId="metric-pipeline" side="top" fullWidth={fullWidth} disableOnMobile={disableGlossaryOnMobile}>
      {barContent}
    </GlossaryTooltip>
  );
};
